import { create } from 'zustand';
import type { DriverWithDisplayStatus } from '../api/driver';

type ViewMode = 'trips' | 'drivers';

interface LoadsListState {
  // Trips view state
  page: number;
  limit: number;
  sortBy: string;
  selectedIds: string[];
  selectedLoadId: string | null;

  // Table filters and columns
  columnFilters: Record<string, string>;
  visibleColumns: string[];

  // View mode
  viewMode: ViewMode;

  // Drivers view - drivers with loads table
  withLoadsPage: number;
  withLoadsLimit: number;

  // Drivers view - available drivers table
  availablePage: number;
  availableLimit: number;

  // Selected driver for panel
  selectedDriver: DriverWithDisplayStatus | null;
}

interface LoadsListActions {
  // Trips view actions
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setSortBy: (sortBy: string) => void;
  setSelectedIds: (ids: string[]) => void;
  setSelectedLoadId: (id: string | null) => void;

  // Table filters and columns actions
  setColumnFilter: (column: string, value: string) => void;
  setColumnFilters: (filters: Record<string, string>) => void;
  clearColumnFilters: () => void;
  setVisibleColumns: (columns: string[]) => void;
  toggleColumn: (column: string) => void;

  // View mode actions
  setViewMode: (mode: ViewMode) => void;

  // Drivers with loads table actions
  setWithLoadsPage: (page: number) => void;
  setWithLoadsLimit: (limit: number) => void;

  // Available drivers table actions
  setAvailablePage: (page: number) => void;
  setAvailableLimit: (limit: number) => void;

  // Selected driver actions
  setSelectedDriver: (driver: DriverWithDisplayStatus | null) => void;

  // Utility actions
  resetTripsState: () => void;
  resetDriversState: () => void;
  resetAll: () => void;
}

type LoadsListStore = LoadsListState & LoadsListActions;

// Default visible columns for the loads table
const DEFAULT_VISIBLE_COLUMNS = [
  'loadNumber',
  'status',
  'pickupDate',
  'originLocation',
  'destinationLocation',
  'deliveryDate',
  'customerName',
  'driverName',
  'truckNumber',
  'equipmentType',
  'totalRevenue',
  'totalMiles',
];

const initialTripsState = {
  page: 1,
  limit: 100,
  sortBy: 'pickupDate',
  selectedIds: [] as string[],
  selectedLoadId: null as string | null,
  columnFilters: {} as Record<string, string>,
  visibleColumns: DEFAULT_VISIBLE_COLUMNS,
};

const initialDriversState = {
  withLoadsPage: 1,
  withLoadsLimit: 100,
  availablePage: 1,
  availableLimit: 100,
  selectedDriver: null as DriverWithDisplayStatus | null,
};

const initialState: LoadsListState = {
  ...initialTripsState,
  viewMode: 'trips',
  ...initialDriversState,
};

export const useLoadsListStore = create<LoadsListStore>()((set) => ({
  ...initialState,

  // Trips view actions
  setPage: (page) => set({ page }),
  setLimit: (limit) => set({ limit, page: 1 }),
  setSortBy: (sortBy) => set({ sortBy }),
  setSelectedIds: (selectedIds) => set({ selectedIds }),
  setSelectedLoadId: (selectedLoadId) => set({ selectedLoadId }),

  // Table filters and columns actions
  setColumnFilter: (column, value) =>
    set((state) => ({
      columnFilters: { ...state.columnFilters, [column]: value },
    })),
  setColumnFilters: (columnFilters) => set({ columnFilters }),
  clearColumnFilters: () => set({ columnFilters: {} }),
  setVisibleColumns: (visibleColumns) => set({ visibleColumns }),
  toggleColumn: (column) =>
    set((state) => ({
      visibleColumns: state.visibleColumns.includes(column)
        ? state.visibleColumns.filter((c) => c !== column)
        : [...state.visibleColumns, column],
    })),

  // View mode actions
  setViewMode: (viewMode) => set({ viewMode }),

  // Drivers with loads table actions
  setWithLoadsPage: (withLoadsPage) => set({ withLoadsPage }),
  setWithLoadsLimit: (withLoadsLimit) => set({ withLoadsLimit, withLoadsPage: 1 }),

  // Available drivers table actions
  setAvailablePage: (availablePage) => set({ availablePage }),
  setAvailableLimit: (availableLimit) => set({ availableLimit, availablePage: 1 }),

  // Selected driver actions
  setSelectedDriver: (selectedDriver) => set({ selectedDriver }),

  // Utility actions
  resetTripsState: () => set(initialTripsState),
  resetDriversState: () => set(initialDriversState),
  resetAll: () => set(initialState),
}));
